<template>
  <div
    class="base-card"
    :class="[{ fill, flush, 'clip-content': clipContent }, `padding-${padding}`, `radius-${radius}`]"
    :style="{ '--background': backgroundStyle, '--color': colorStyle, '--border-color': borderStyle }"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

/**
 * Card container component with customizable background, text, and border colors
 *
 * Provides a styled card with rounded corners, padding, and support for
 * design system colors. Variant prop controls the shade intensity.
 *
 * @example Basic card
 * ```vue
 * <BaseCard>
 *   <h2>Card Title</h2>
 *   <p>Card content goes here</p>
 * </BaseCard>
 * ```
 *
 * @example Colored background
 * ```vue
 * <BaseCard background="blue" variant="light">
 *   Light blue background card
 * </BaseCard>
 * ```
 *
 * @example Card with border
 * ```vue
 * <BaseCard border-color="primary">
 *   Card with primary blue border
 * </BaseCard>
 * ```
 *
 * @example Error card with matching border
 * ```vue
 * <BaseCard background="red" variant="light" border-color="error">
 *   Error message with red border
 * </BaseCard>
 * ```
 *
 * @example Full height card
 * ```vue
 * <BaseCard fill>
 *   This card fills its parent container height
 * </BaseCard>
 * ```
 *
 * @example Card with custom padding
 * ```vue
 * <BaseCard padding="sm">
 *   Compact card with sm padding
 * </BaseCard>
 * ```
 *
 * @example Card with no padding
 * ```vue
 * <BaseCard padding="none">
 *   <img src="banner.jpg" alt="Banner" />
 * </BaseCard>
 * ```
 *
 * @example Card with custom border radius
 * ```vue
 * <BaseCard radius="lg">
 *   Large rounded corners
 * </BaseCard>
 * ```
 *
 * @example Pill-shaped card
 * ```vue
 * <BaseCard radius="full" padding="sm">
 *   Fully rounded pill card
 * </BaseCard>
 * ```
 */
export interface BaseCardProps {
  /**
   * Background color from design system
   * - light variant uses shade 100 (very light)
   * - dark variant uses shade 400 (medium)
   * @default null (uses default white background)
   */
  background?: 'white' | 'blue' | 'red' | 'amber' | 'green' | 'teal' | 'purple' | 'pink' | 'gray' | 'yellow' | null;

  /**
   * Text color from design system (optional override)
   * If not set, automatically uses light or dark text based on variant
   * @default null (auto-determined by variant)
   */
  color?: 'white' | 'blue' | 'red' | 'amber' | 'green' | 'teal' | 'purple' | 'pink' | 'gray' | 'yellow' | null;

  /**
   * Border color from design system using semantic colors
   * Uses shade 300 for optimal contrast
   * - primary: Blue border for primary content
   * - secondary: Orange border for secondary emphasis
   * - error: Red border for errors and warnings
   * - warning: Yellow border for cautions
   * - success: Green border for success states
   * - info: Purple border for informational content
   * - neutral: Gray border for neutral content
   * @default null (no border)
   */
  border?: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info' | 'neutral' | null;

  /**
   * Color intensity variant
   * - light: Uses color shade 100 for backgrounds, dark text
   * - dark: Uses color shade 400 for backgrounds, light text
   * @default 'light'
   */
  variant?: 'light' | 'dark';

  /**
   * Makes card fill parent container height (removes bottom margin)
   * @default false
   */
  fill?: boolean;

  /**
   * Removes bottom margin for flush positioning
   * Useful when card is nested inside another container
   * @default false
   */
  flush?: boolean;

  /**
   * Padding size for card content
   * Maps to design system gap tokens
   * - none: No padding (0)
   * - sm: 1rem (16px)
   * - md: 2rem (32px) - default
   * - lg: 4rem (64px)
   * @default 'md'
   */
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg';

  /**
   * Border radius size for card corners
   * Maps to design system border radius tokens
   * - none: No radius (0) - sharp corners
   * - sm: 4px - subtle rounding
   * - md: 8px - default rounding
   * - lg: 16px - prominent rounding
   * - full: 9999px - pill shape
   * @default 'md'
   */
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';

  /**
   * Clips overflowing content to the card's bounds (applies `overflow: hidden`)
   * Enable for full-bleed images or edge-to-edge lists that should respect
   * the card's rounded corners. Leave off when nesting tooltips, popovers, or
   * children whose box-shadow/absolute positioning should escape the card.
   * @default false
   */
  clipContent?: boolean;
}

const props = withDefaults(defineProps<BaseCardProps>(), {
  background: null,
  color: null,
  border: null,
  variant: 'light',
  fill: false,
  flush: false,
  padding: 'md',
  radius: 'md',
  clipContent: false,
});

const backgroundStyle = computed(() => {
  if (!props.background) return;
  if (props.background === 'white') return `var(--color-white)`;
  const variantValue = props.variant === 'light' ? '100' : '400';
  return `var(--color-${props.background}-${variantValue})`;
});

const colorStyle = computed(() => {
  if (props.color) {
    if (props.color === 'white') return `var(--color-white)`;
    const variantValue = props.variant === 'light' ? '400' : '100';
    return `var(--color-${props.color}-${variantValue})`;
  }
  if (!props.background) return undefined;
  return props.variant === 'light' ? `var(--color-dark)` : `var(--color-light)`;
});

const borderStyle = computed(() => {
  if (!props.border) return;
  return `var(--color-${props.border}-300)`;
});
</script>

<style>
.base-card {
  background-color: var(--background, var(--color-light));
  border: var(--size-border-thin) solid var(--border-color, transparent);
  margin-bottom: var(--gap-md);
  color: var(--color);
  transition: var(--transition-default);

  &.clip-content {
    overflow: hidden;
  }

  &.padding-none {
    padding: 0;
  }

  &.padding-xs {
    padding: var(--gap-xs);
  }

  &.padding-sm {
    padding: var(--gap-sm);
  }

  &.padding-md {
    padding: var(--gap-md);
  }

  &.padding-lg {
    padding: var(--gap-lg);
  }

  &.radius-none {
    border-radius: 0;
  }

  &.radius-sm {
    border-radius: var(--border-radius-sm);
  }

  &.radius-md {
    border-radius: var(--border-radius-md);
  }

  &.radius-lg {
    border-radius: var(--border-radius-lg);
  }

  &.radius-full {
    border-radius: var(--border-radius-full);
  }

  &.fill {
    height: 100%;
    margin-bottom: 0;
  }

  &.flush,
  &:last-child {
    margin-bottom: 0;
  }
}
</style>
